import { useEffect, useMemo, useState } from "react";
import client from "../api/client";

const companyId = () => Number(localStorage.getItem("companyId") || 0);

const val = (r, ...keys) =>
  keys.map((k) => r?.[k]).find((x) => x !== undefined && x !== null) ?? "";

const safe = (v) =>
  v === null || v === undefined ? "" : typeof v === "object" ? "" : String(v);

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const money = (v) =>
  num(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.result)) return data.result;
  return [];
};

const doc = (r) => safe(val(r, "DocType", "docType")).toUpperCase().trim();

export default function OrderTrackingPage() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [docType, setDocType] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      setLoading(true);

      const res = await client.get("/sales/order-tracking", {
        params: { companyId: companyId() || undefined },
      });

      setRows(getArray(res.data));
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.title ||
        e?.message ||
        "Failed to load order tracking";

      alert(msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    let data = Array.isArray(rows) ? rows : [];

    if (docType) {
      data = data.filter((x) => doc(x) === docType);
    }

    const s = search.toLowerCase().trim();
    if (s) {
      data = data.filter((r) =>
        Object.values(r).map(safe).join(" ").toLowerCase().includes(s)
      );
    }

    return data;
  }, [rows, search, docType]);

  const stats = useMemo(() => {
    const data = Array.isArray(rows) ? rows : [];

    return {
      total: data.length,
      quotation: data.filter((x) => doc(x) === "QUOTATION").length,
      order: data.filter((x) => doc(x) === "SALES_ORDER").length,
      delivery: data.filter((x) => doc(x) === "DELIVERY_CHALLAN").length,
      invoice: data.filter((x) => doc(x) === "SALES_INVOICE").length,
    };
  }, [rows]);

  const badge = (status) => {
    const s = safe(status).toUpperCase().trim();

    if (["APPROVED", "DELIVERED", "INVOICED", "CONVERTED", "COMPLETED"].includes(s))
      return "bg-success";

    if (["PENDING", "OPEN", "PARTIAL", "PARTIALLY_DELIVERED"].includes(s))
      return "bg-warning text-dark";

    if (["CANCELLED", "REJECTED", "VOID"].includes(s)) return "bg-danger";

    return "bg-secondary";
  };

  const docLabel = (type) => {
    const t = safe(type).toUpperCase().trim();

    if (t === "QUOTATION") return "Quotation";
    if (t === "SALES_ORDER") return "Sales Order";
    if (t === "DELIVERY_CHALLAN") return "Delivery Challan";
    if (t === "SALES_INVOICE") return "Sales Invoice";

    return t || "-";
  };

  return (
    <div className="container-fluid py-2" style={{ fontSize: 11, padding: 0 }}>
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 2px 10px rgba(0,0,0,.08)",
        }}
      >
        <div
          style={{
            background:
              "linear-gradient(90deg, rgb(11, 79, 74), rgb(15, 118, 110))",
            color: "#fff",
            padding: "9px 12px",
          }}
        >
          <h6 style={{ fontSize: 13, fontWeight: 800, margin: 0 }}>
            <i className="bi bi-diagram-3 me-2"></i>
            Order Tracking
          </h6>
          <div style={{ fontSize: 10, opacity: 0.85 }}>
            Track quotation, sales order, delivery challan and invoice workflow
          </div>
        </div>

        <div style={{ padding: 10 }}>
          <div className="row g-2 mb-2">
            <Stat title="Total Docs" value={stats.total} />
            <Stat title="Quotations" value={stats.quotation} />
            <Stat title="Sales Orders" value={stats.order} />
            <Stat title="Deliveries" value={stats.delivery} />
          </div>

          <div className="row g-2 mb-2">
            <div className="col-md-3">
              <label style={label}>Document Type</label>
              <select
                className="form-select form-select-sm"
                value={docType}
                onChange={(e) => setDocType(e.target.value)}
              >
                <option value="">All</option>
                <option value="QUOTATION">Quotation</option>
                <option value="SALES_ORDER">Sales Order</option>
                <option value="DELIVERY_CHALLAN">Delivery Challan</option>
                <option value="SALES_INVOICE">Sales Invoice</option>
              </select>
            </div>

            <div className="col-md-5">
              <label style={label}>Search</label>
              <input
                className="form-control form-control-sm"
                placeholder="Document no / customer / status"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="col-md-2 d-flex align-items-end">
              <button
                className="btn btn-primary btn-sm w-100"
                onClick={load}
                disabled={loading}
              >
                {loading ? "Loading..." : "Refresh"}
              </button>
            </div>

            <div className="col-md-2 d-flex align-items-end justify-content-end">
              <span className="text-muted">
                Rows: <b>{filtered.length}</b>
              </span>
            </div>
          </div>

          <div
            style={{
              maxHeight: 520,
              overflow: "auto",
              border: "1px solid #e9ecef",
              borderRadius: 10,
            }}
          >
            <table
              className="table table-bordered table-hover align-middle"
              style={{ fontSize: 11, minWidth: 1200, marginBottom: 0 }}
            >
              <thead>
                <tr>
                  {[
                    "Type",
                    "Document No",
                    "Date",
                    "Customer",
                    "Amount",
                    "Status",
                    "Notes",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        fontSize: 10,
                        color: "#fff",
                        background: "#07111f",
                        padding: "7px 8px",
                        whiteSpace: "nowrap",
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                      }}
                      className={h === "Amount" ? "text-end" : ""}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="text-center text-muted"
                      style={{ padding: 18 }}
                    >
                      {loading ? "Loading..." : "No tracking records found"}
                    </td>
                  </tr>
                ) : (
                  filtered.map((r, i) => (
                    <tr key={`${doc(r)}-${safe(val(r, "Id", "id", "DocNo", "docNo"))}-${i}`}>
                      <td>
                        <b>{docLabel(val(r, "DocType", "docType"))}</b>
                      </td>
                      <td>{safe(val(r, "DocNo", "docNo")) || "-"}</td>
                      <td>
                        {String(val(r, "DocDate", "docDate") || "").slice(0, 10) ||
                          "-"}
                      </td>
                      <td>{safe(val(r, "CustomerName", "customerName")) || "-"}</td>
                      <td className="text-end">
                        {money(val(r, "GrandTotal", "grandTotal", "Amount", "amount"))}
                      </td>
                      <td>
                        <span
                          className={`badge ${badge(val(r, "Status", "status"))}`}
                        >
                          {safe(val(r, "Status", "status")) || "OPEN"}
                        </span>
                      </td>
                      <td>{safe(val(r, "Notes", "notes")) || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>

              <tfoot>
                <tr style={{ background: "#f8fafc", fontWeight: 800 }}>
                  <td colSpan="4" className="text-end">
                    Total
                  </td>
                  <td className="text-end">
                    {money(
                      filtered.reduce(
                        (sum, r) =>
                          sum +
                          num(val(r, "GrandTotal", "grandTotal", "Amount", "amount")),
                        0
                      )
                    )}
                  </td>
                  <td colSpan="2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

const label = {
  fontSize: 10,
  fontWeight: 700,
  marginBottom: 3,
};

function Stat({ title, value }) {
  return (
    <div className="col-md-3">
      <div
        style={{
          border: "1px solid #e9ecef",
          background: "#f8fafc",
          borderRadius: 10,
          padding: 9,
          borderTop: "3px solid #0f766e",
        }}
      >
        <div style={{ fontSize: 10, color: "#64748b", fontWeight: 800 }}>
          {title}
        </div>
        <div style={{ fontSize: 15, color: "#0f766e", fontWeight: 900 }}>
          {value}
        </div>
      </div>
    </div>
  );
}